import { BaseComponent, AppStartupService } from '@ridango/core';
import { MomentDatePipe } from '@ridango/core/lib/pipes/moment-date.pipe';
import { MoneyFormatPipe } from '@ridango/core/lib/pipes/money-format.pipe';
import { DurationPipe } from '@ridango/core/lib/pipes/duration.pipe';
import { DistancePipe } from '@ridango/core/lib/pipes/distance.pipe';
import { ItineraryLegsOverviewUtils } from './itinerary-legs-overview.utils';
import { Component, OnInit, OnDestroy, OnChanges, Input, Inject, SimpleChanges } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import { Temporal } from '@js-temporal/polyfill';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import {
    Itinerary,
    Leg,
    TripDetailsTime,
    JourneyPlannerItineraryTransportMode,
    OpenTripPlannerFacade
} from '@ridango/components';
import Env from '@ridango/core/env';
import { ScreenReader } from '@capacitor/screen-reader';

@Component({
    selector: 'rwidget-itinerary-legs-overview',
    templateUrl: './itinerary-legs-overview.component.html',
    providers: [MomentDatePipe, MoneyFormatPipe, DurationPipe, DistancePipe]
})
export class ItineraryLegsOverviewComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
    @Input() itinerary: Itinerary;
    @Input() legs: Leg[] = [];
    @Input() legTripIdToDetailsMap: Map<string, TripDetailsTime[]> = new Map();

    updatedLegs: Leg[] = [];
    legAriaLabels: string[] = [];
    legMode = JourneyPlannerItineraryTransportMode;
    isRealtime: boolean;
    isScreenReaderEnabled = false;
    liveAnnouncement = '';
    search$ = this.openTripPlannerFacade.search$;
    search;
    private expandedStopsByLegIndex = new Set<number>();
    private screenReaderStateListener: PluginListenerHandle | null = null;

    constructor(
        @Inject('ENV') public env: Env,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private momentDatePipe: MomentDatePipe,
        private moneyFormatPipe: MoneyFormatPipe,
        private durationPipe: DurationPipe,
        private distancePipe: DistancePipe,
        private translate: TranslateService,
        private appStartup: AppStartupService
    ) {
        super();
    }

    ngOnInit(): void {
        this.rebuildLegsAndAriaLabels();
        this.initSubscriptions();
        this.initScreenReader();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.legs || changes.itinerary) {
            this.rebuildLegsAndAriaLabels();
        }
    }

    ngOnDestroy(): void {
        this.screenReaderStateListener?.remove();
        this.screenReaderStateListener = null;
        super.ngOnDestroy();
    }

    get isStopBusOrigin(): boolean {
        const firstElement = this.updatedLegs[0];

        return (
            this.search?.origin?.useLocationFromMap ||
            this.search?.origin?.useCurrentLocation ||
            this.isWalkingLegs(firstElement)
        );
    }

    get isStopBusDestination(): boolean {
        const lastElement = this.updatedLegs[this.updatedLegs.length - 1];

        return (
            this.search?.destination?.useLocationFromMap ||
            this.search?.destination?.useCurrentLocation ||
            this.isWalkingLegs(lastElement)
        );
    }

    private isWalkingLegs(leg: Leg): boolean {
        return leg?.mode === this.legMode.Walk;
    }

    realDataStartTime(leg: Leg): number {
        return leg.startTime + leg.departureDelay * 1000;
    }

    realDataEndTime(leg: Leg): number {
        return leg.endTime + leg.arrivalDelay * 1000;
    }

    private buildLegAriaLabel(legN: number, legData: Leg): string {
        return ItineraryLegsOverviewUtils.buildLegAriaLabel({
            legN,
            legMode: legData?.mode,
            legData,
            originalLeg: this.legs?.[legN - 1],
            nextLeg: this.updatedLegs?.[legN],
            totalLegs: this.updatedLegs?.length || 0,
            originUseLocationFromMap: this.search?.origin?.useLocationFromMap,
            destinationUseLocationFromMap: this.search?.destination?.useLocationFromMap,
            screenReaderEnabled: this.isScreenReaderEnabled,
            includeIntermediateStopsInAria: this.isStopsIncludedInAria(legN - 1),
            timezone: this.appStartup.getTimezone(),
            legModeMap: this.legMode,
            translate: this.translate.instant.bind(this.translate),
            formatDate: (value: Date | string | number, format?: string, timezone?: string) =>
                this.momentDatePipe.transform(value, format, timezone),
            formatMoney: (value: number | string | { amount: number }, multiplier?: number) =>
                this.moneyFormatPipe.transform(value, multiplier),
            formatDuration: (value: string | number | Temporal.Duration) => this.durationPipe.transform(value),
            formatDistance: (value: number) => this.distancePipe.transform(value),
            locale: this.appStartup.getLocale()
        });
    }

    /*
     * Handles keyboard/screen reader activation and toggles intermediate stops in the leg aria label.
     */
    onLegA11yActivate(event: Event, legIndex: number, leg: Leg): void {
        if (!this.canToggleStopsViaA11y(leg)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (this.isStopsIncludedInAria(legIndex)) {
            this.expandedStopsByLegIndex.delete(legIndex);
            this.updateAriaLabelAtIndex(legIndex);
            this.announce(
                this.legAriaLabels[legIndex] +
                    '. ' +
                    this.translate.instant('TRAVEL_PLANNER_WCAG_DOUBLE_TAP_SHOW_STOPS', {
                        count: leg.intermediateStops.length
                    })
            );
            return;
        }

        this.expandedStopsByLegIndex.add(legIndex);
        this.updateAriaLabelAtIndex(legIndex);
        this.announce(
            this.legAriaLabels[legIndex] + '. ' + this.translate.instant('TRAVEL_PLANNER_WCAG_DOUBLE_TAP_HIDE_STOPS')
        );
    }

    /*
     * Clears then sets liveAnnouncement so that screen readers detect the DOM change
     * even when the same message is announced twice in a row.
     */
    private announce(message: string): void {
        this.liveAnnouncement = '';
        setTimeout(() => {
            this.liveAnnouncement = message;
        });
    }

    canToggleStopsViaA11y(leg: Leg): boolean {
        return !!leg?.intermediateStops?.length && (!!leg?.tripId || leg?.mode === this.legMode.Flexible);
    }

    isStopsIncludedInAria(legIndex: number): boolean {
        return this.expandedStopsByLegIndex.has(legIndex);
    }

    private async initScreenReader(): Promise<void> {
        try {
            const { value } = await ScreenReader.isEnabled();
            this.isScreenReaderEnabled = !!value;
            this.rebuildAriaLabels();

            this.screenReaderStateListener = await ScreenReader.addListener('stateChange', ({ value }) => {
                this.isScreenReaderEnabled = !!value;
                if (!value) {
                    this.expandedStopsByLegIndex.clear();
                }
                this.rebuildAriaLabels();
            });
        } catch {
            this.isScreenReaderEnabled = false;
            this.rebuildAriaLabels();
        }
    }

    private rebuildLegsAndAriaLabels(): void {
        this.isRealtime = this.env.JOURNEY_PLANNER?.REALTIME_AVAILABLE && this.itinerary?.realTime;
        this.updatedLegs = this.legs?.map((leg) => {
            if (leg.departureDelay !== 0 || leg.arrivalDelay !== 0) {
                return {
                    ...leg,
                    startTime: this.realDataStartTime(leg),
                    endTime: this.realDataEndTime(leg)
                };
            }
            return leg;
        });
        this.rebuildAriaLabels();
    }

    private rebuildAriaLabels(): void {
        this.legAriaLabels = (this.updatedLegs || []).map((leg, index) => this.buildLegAriaLabel(index + 1, leg));
    }

    private updateAriaLabelAtIndex(index: number): void {
        if (index < 0 || index >= this.updatedLegs.length) {
            return;
        }
        this.legAriaLabels[index] = this.buildLegAriaLabel(index + 1, this.updatedLegs[index]);
    }

    private initSubscriptions(): void {
        this.search$.pipe(takeUntil(this.componentDestroyed$)).subscribe((value) => {
            const prevOriginFromMap = this.search?.origin?.useLocationFromMap;
            const prevDestinationFromMap = this.search?.destination?.useLocationFromMap;
            this.search = value;
            const nextOriginFromMap = this.search?.origin?.useLocationFromMap;
            const nextDestinationFromMap = this.search?.destination?.useLocationFromMap;
            if (prevOriginFromMap !== nextOriginFromMap || prevDestinationFromMap !== nextDestinationFromMap) {
                this.rebuildAriaLabels();
            }
        });
    }
}
